import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Search, CheckCircle, XCircle, AlertTriangle, User, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';

export default function SellersJsonChecker() {
  const [domain, setDomain] = useState('');
  const [sellerId, setSellerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [lookupResult, setLookupResult] = useState(null);
  const [tab, setTab] = useState('check');
  const [issuesExpanded, setIssuesExpanded] = useState(true); // Issues card open by default

  // Fetch tab search — backend powered so it searches ALL sellers
  const [search, setSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // null = show default table
  const debounceRef = useRef(null);

  async function handleCheck() {
    if (!domain.trim()) return toast.error('Enter a domain');
    setLoading(true); setResult(null); setSearchResults(null); setSearch('');
    try {
      const { data } = await axios.post('/api/sellers-json/check', { domain: domain.trim() });
      setResult(data);
      if (data.success) toast.success(`${data.summary.totalSellers.toLocaleString()} sellers loaded`);
      else toast.error(data.error || 'Failed');
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }

  // When search changes, debounce a backend lookup so ALL sellers are searched
  useEffect(() => {
    if (!result?.success || !domain.trim()) return;

    if (!search.trim()) {
      setSearchResults(null); // reset to default table
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await axios.post('/api/sellers-json/lookup-seller', {
          domain: domain.trim(),
          sellerId: search.trim()
        });
        setSearchResults(data);
      } catch { setSearchResults({ found: false, totalSellersSearched: 0 }); }
      finally { setSearchLoading(false); }
    }, 500); // 500ms debounce

    return () => clearTimeout(debounceRef.current);
  }, [search, domain, result]);

  async function handleLookup() {
    if (!domain.trim() || !sellerId.trim()) return toast.error('Enter domain and seller ID');
    setLookupLoading(true); setLookupResult(null);
    try {
      const { data } = await axios.post('/api/sellers-json/lookup-seller', { domain: domain.trim(), sellerId: sellerId.trim() });
      setLookupResult(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally { setLookupLoading(false); }
  }

  // Decide what to show in the sellers table
  const isTruncated = result?.sellers?.length < result?.summary?.totalSellers;
  const displaySellers = searchResults
    ? (searchResults.matches?.length > 1 ? searchResults.matches : searchResults.seller ? [searchResults.seller] : [])
    : (result?.sellers || []);

  function SellerRow({ s, highlight }) {
    const h = highlight?.toLowerCase();
    function hl(str) {
      if (!str || !h) return str || '—';
      const idx = str.toLowerCase().indexOf(h);
      if (idx === -1) return str;
      return <>{str.slice(0, idx)}<mark style={{ background:'color-mix(in srgb, var(--cyan) 25%, transparent)', color:'var(--cyan)', borderRadius:'2px' }}>{str.slice(idx, idx+h.length)}</mark>{str.slice(idx+h.length)}</>;
    }
    return (
      <tr>
        <td className="mono" style={{ color: 'var(--cyan)' }}>{hl(String(s.seller_id || ''))}</td>
        <td style={{ color: 'var(--text-primary)' }}>{hl(s.name) || '—'}</td>
        <td className="text-muted">{hl(s.domain) || '—'}</td>
        <td>
          <span className={`badge ${s.seller_type === 'PUBLISHER' ? 'badge-green' : s.seller_type === 'INTERMEDIARY' ? 'badge-purple' : 'badge-cyan'}`}>
            {s.seller_type || '?'}
          </span>
        </td>
        <td>{s.is_confidential ? <span className="badge badge-amber">Yes</span> : <span className="badge badge-muted">No</span>}</td>
      </tr>
    );
  }

  return (
    <div>
      <h1 className="page-title">Sellers.json Checker</h1>
      <p className="page-subtitle">Fetch and validate sellers.json files from any SSP. Search by Seller ID, Name, or Domain — searches ALL sellers, no cap.</p>

      <div className="tabs">
        <button className={`tab ${tab === 'check' ? 'active' : ''}`} onClick={() => setTab('check')}>Fetch Sellers.json</button>
        <button className={`tab ${tab === 'lookup' ? 'active' : ''}`} onClick={() => setTab('lookup')}>Seller ID Lookup</button>
      </div>

      {tab === 'check' && (
        <div className="card mb-4" style={{ marginBottom: '16px' }}>
          <div className="input-group">
            <input className="input-field" placeholder="e.g. rubiconproject.com, pubmatic.com"
              value={domain} onChange={e => setDomain(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCheck()} />
            <button className="btn btn-primary" onClick={handleCheck} disabled={loading}>
              {loading ? <span className="spinner" /> : <Search size={14} />}
              {loading ? 'Fetching...' : 'Fetch'}
            </button>
          </div>
        </div>
      )}

      {tab === 'lookup' && (
        <div className="card mb-4" style={{ marginBottom: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
            <input className="input-field" placeholder="SSP Domain (e.g. openx.com)"
              value={domain} onChange={e => setDomain(e.target.value)} />
            <input className="input-field" placeholder="Seller ID, Name or Domain (partial ok)"
              value={sellerId} onChange={e => setSellerId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLookup()} />
          </div>
          <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'8px' }}>
            Search by Seller ID (exact), Publisher Name (partial match), or Publisher Domain
          </div>
          <button className="btn btn-primary" onClick={handleLookup} disabled={lookupLoading}>
            {lookupLoading ? <><span className="spinner"/> Looking up...</> : <><Search size={14}/> Search Seller</>}
          </button>

          {lookupResult && (
            <div style={{ marginTop: '16px' }}>
              <div className={`alert ${lookupResult.found ? 'alert-success' : 'alert-error'}`}>
                {lookupResult.found ? <CheckCircle size={14} /> : <XCircle size={14} />}
                {lookupResult.found
                  ? `✓ ${lookupResult.matchCount > 1 ? lookupResult.matchCount + ' sellers' : '1 seller'} found in ${lookupResult.domain || domain} (${(lookupResult.totalSellersSearched || 0).toLocaleString()} sellers searched)`
                  : `✗ "${sellerId}" not found in ${lookupResult.domain || domain} (${(lookupResult.totalSellersSearched || 0).toLocaleString()} sellers searched)`}
              </div>
              {lookupResult.url && (
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
                  Source: <span style={{ color: 'var(--cyan)' }}>{lookupResult.url}</span>
                </div>
              )}
              {(lookupResult.matches?.length > 1 ? lookupResult.matches : lookupResult.seller ? [lookupResult.seller] : []).map((seller, idx) => (
                <div key={idx} className="card" style={{ marginTop: '8px', background: 'var(--bg-base)' }}>
                  {[
                    ['Seller ID', seller.seller_id],
                    ['Name', seller.name],
                    ['Domain', seller.domain],
                    ['Type', seller.seller_type],
                    ['Confidential', seller.is_confidential ? 'Yes' : 'No'],
                  ].map(([k, v]) => v && (
                    <div key={k} style={{ display: 'flex', gap: '12px', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: '12px' }}>
                      <span style={{ minWidth: '110px', color: 'var(--text-muted)' }}>{k}</span>
                      <span style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{String(v)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {result && result.success && (
        <>
          <div className="grid-4" style={{ marginBottom: '16px' }}>
            <div className="stat-card cyan"><div className="stat-label">Total Sellers</div><div className="stat-value">{result.summary.totalSellers.toLocaleString()}</div></div>
            <div className="stat-card green"><div className="stat-label">Publishers</div><div className="stat-value green">{result.summary.publishers.toLocaleString()}</div></div>
            <div className="stat-card amber"><div className="stat-label">Intermediaries</div><div className="stat-value amber">{result.summary.intermediaries.toLocaleString()}</div></div>
            <div className="stat-card red"><div className="stat-label">Issues</div><div className="stat-value red">{result.summary.issues}</div></div>
          </div>

          <div className="card mb-4" style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '12px', fontSize: '12px', flexWrap: 'wrap' }}>
              {result.contact_email && <span><span style={{ color: 'var(--text-muted)' }}>Contact: </span>{result.contact_email}</span>}
              {result.version && <span><span style={{ color: 'var(--text-muted)' }}>Version: </span>{result.version}</span>}
              <span><span style={{ color: 'var(--text-muted)' }}>Confidential: </span>{result.summary.confidential}</span>
              <span className="badge badge-muted">{result.fetchTimeMs}ms</span>
            </div>
          </div>

          {result.issues && result.issues.length > 0 && (
            <div className="card mb-4" style={{ marginBottom: '12px', borderColor: 'color-mix(in srgb, var(--red) 30%, transparent)' }}>
              <div
                className="card-title"
                style={{ marginBottom: issuesExpanded ? '10px' : 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', userSelect: 'none' }}
                onClick={() => setIssuesExpanded(v => !v)}>
                {issuesExpanded ? <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />}
                <AlertTriangle size={14} style={{ color: 'var(--red)' }} />
                Issues <span className="badge badge-red">{result.issues.length}</span>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 400, marginLeft: '2px' }}>
                  {issuesExpanded ? '· click to collapse' : '· click to expand'}
                </span>
              </div>
              {issuesExpanded && <>
              {result.issues.slice(0, 20).map((issue, i) => (
                <div key={i} className="alert alert-error" style={{ marginBottom: '4px', flexWrap: 'wrap' }}>
                  <XCircle size={12} />
                  <span style={{ flex: 1 }}>
                    <strong>{issue.field}</strong>: {issue.message}
                    <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', fontFamily: 'var(--font-mono)' }}>
                      {issue.seller_id != null && issue.seller_id !== ''
                        ? <>Seller ID: <span style={{ color: 'var(--cyan)' }}>{String(issue.seller_id)}</span></>
                        : <>Seller ID: <span style={{ color: 'var(--text-muted)' }}>(none)</span></>}
                      {issue.name ? <> · Name: {issue.name}</> : null}
                      {issue.seller_type ? <> · Type: {issue.seller_type}</> : null}
                      {issue.line ? <> · Entry #{issue.line}</> : null}
                    </span>
                  </span>
                </div>
              ))}
              {result.issues.length > 20 && (
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '8px', textAlign: 'center' }}>
                  Showing first 20 of {result.issues.length} issues. Use the seller search below to inspect specific sellers.
                </div>
              )}
              </>}
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <div className="card-title" style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                <User size={14} />
                Sellers
                {/* Search status badges */}
                {!search && isTruncated && (
                  <span className="badge badge-amber">
                    First {result.sellers.length.toLocaleString()} of {result.summary.totalSellers.toLocaleString()} shown
                  </span>
                )}
                {search && !searchLoading && searchResults && (
                  <span className={`badge ${searchResults.found ? 'badge-green' : 'badge-red'}`}>
                    {searchResults.found
                      ? `${searchResults.matchCount || 1} result${(searchResults.matchCount||1)>1?'s':''} found across all ${(searchResults.totalSellersSearched||0).toLocaleString()} sellers`
                      : `No results in ${(searchResults.totalSellersSearched||0).toLocaleString()} sellers`}
                  </span>
                )}
                {searchLoading && (
                  <span className="badge badge-cyan">
                    <RefreshCw size={10} style={{ animation:'spin 1s linear infinite', verticalAlign:'middle', marginRight:'3px' }}/>
                    Searching all {result.summary.totalSellers.toLocaleString()} sellers...
                  </span>
                )}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <input
                  className="input-field"
                  style={{ width: '240px' }}
                  placeholder="Search by ID, name or domain..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <button className="btn btn-secondary btn-sm" onClick={() => { setSearch(''); setSearchResults(null); }}>✕</button>
                )}
              </div>
            </div>

            {!search && isTruncated && (
              <div style={{ padding:'8px 14px', background:'color-mix(in srgb, var(--amber) 6%, transparent)', borderBottom:'1px solid var(--border)', fontSize:'12px', color:'var(--amber)' }}>
                ⚡ Only the first {result.sellers.length.toLocaleString()} sellers are shown in the table. Use the search box above to search across <strong>all {result.summary.totalSellers.toLocaleString()} sellers</strong> — it searches the full file on the server.
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Seller ID</th><th>Name</th><th>Domain</th><th>Type</th><th>Confidential</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults
                    ? displaySellers.map((s, i) => <SellerRow key={i} s={s} highlight={search} />)
                    : displaySellers.slice(0, 200).map((s, i) => <SellerRow key={i} s={s} highlight={search} />)
                  }
                </tbody>
              </table>
              {displaySellers.length === 0 && !searchLoading && (
                <div className="empty-state">
                  <div className="empty-state-text">
                    {search ? `No sellers found matching "${search}"` : 'No sellers'}
                  </div>
                </div>
              )}
              {searchLoading && (
                <div style={{ textAlign:'center', padding:'24px', color:'var(--text-muted)', fontSize:'13px' }}>
                  <RefreshCw size={16} style={{ animation:'spin 1s linear infinite', marginBottom:'8px' }} />
                  <div>Searching all {result.summary.totalSellers.toLocaleString()} sellers...</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {result && !result.success && (
        <div className="alert alert-error">
          <XCircle size={14} /> {result.error} — {result.url}
        </div>
      )}
    </div>
  );
}
